import { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getCobitDomains, loadCobitDomain, submitCobitDomain } from '../services/cobitService';
import { removeEvidenceFile } from '../services/uploadService';
import { getDomainProgress, getResponsesForDomain } from '../utils/cobitProgress';
import { getIncompleteQuestions, validateBeforeSubmit } from '../utils/cobitValidation';
import { useCobitAutoSave } from './useCobitAutoSave';
import { useUploadManager } from './useUploadManager';

const MAX_FILE_SIZE = 150 * 1024 * 1024;

export const useCobitAssessment = (navigate) => {
  const [domains, setDomains] = useState([]);
  const [responses, setResponses] = useState([]);
  const [assessmentIds, setAssessmentIds] = useState({});
  const [submissionStatus, setSubmissionStatus] = useState({});
  const [selectedDomainId, setSelectedDomainId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [token] = useState(localStorage.getItem('access_token'));

  const [saveState, setSaveState] = useState({
    hasUnsyncedChanges: false,
    blockedReason: null,
    lastError: null,
  });

  const getSelectedDomain = useCallback(
    () => domains.find((d) => d.id === selectedDomainId),
    [domains, selectedDomainId]
  );

  const selectedDomain = useMemo(
    () => domains.find((d) => d.id === selectedDomainId),
    [domains, selectedDomainId]
  );

  const markSavedTime = useCallback(() => setLastSaved(new Date().toLocaleTimeString()), []);

  const onResponseIdSaved = useCallback((questionId, id) => {
    setResponses((prev) => prev.map((r) => (r.question === questionId ? { ...r, id } : r)));
  }, []);

  const {
    saveSingleResponse,
    enqueueSave,
    debouncedSave,
    isBlockedByAuth,
    isOffline,
  } = useCobitAutoSave({
    getSelectedDomain,
    assessmentIds,
    submissionStatus,
    onResponseIdSaved,
    onSaved: markSavedTime,
    onSaveStateChange: setSaveState,
  });

  const ensureResponse = useCallback(
    async (questionId) => {
      const existing = responses.find((r) => r.question === questionId);
      if (!existing) return null;
      if (existing.id) return existing.id;
      return saveSingleResponse(existing);
    },
    [responses, saveSingleResponse]
  );

  const onUploadSuccess = useCallback(
    (questionId, uploadedFile) => {
      setResponses((prev) =>
        prev.map((r) =>
          r.question === questionId
            ? { ...r, backendFiles: [...(r.backendFiles || []), uploadedFile] }
            : r
        )
      );
      markSavedTime();
      setSaveState((prev) => ({
        ...prev,
        hasUnsyncedChanges: false,
        blockedReason: null,
        lastError: null,
      }));
    },
    [markSavedTime]
  );

  const uploadManager = useUploadManager({
    responseType: 'cobit',
    concurrency: 3,
    ensureResponse,
    onUploadSuccess,
  });

  useEffect(() => {
    if (isBlockedByAuth || isOffline) uploadManager.pauseAll();
    else if (uploadManager.isPaused) uploadManager.resumeAll();
  }, [isBlockedByAuth, isOffline, uploadManager]);

  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }

    const run = async () => {
      try {
        setLoading(true);
        const domainsRes = await getCobitDomains();

        const domainsData = (domainsRes.data || []).sort((a, b) => {
          const aMatch = a.code?.match(/\d+/);
          const bMatch = b.code?.match(/\d+/);
          if (aMatch && bMatch) return parseInt(aMatch[0], 10) - parseInt(bMatch[0], 10);
          return a.id - b.id;
        });

        setDomains(domainsData);

        const idsMap = {};
        const statusMap = {};
        const allResponses = [];

        for (const domain of domainsData) {
          try {
            const domainRes = await loadCobitDomain(domain.id);
            idsMap[domain.id] = domainRes.data.assessment_id;
            statusMap[domain.id] = domainRes.data.submission_status;

            domainRes.data.questions.forEach((q) => {
              const backendFiles = Array.isArray(q.response?.evidence_files)
                ? q.response.evidence_files
                    .filter((f) => f && f.id)
                    .map((f) => ({
                      id: f.id,
                      name: f.file_name || f.name || 'Unknown File',
                      file_url: f.file_url || f.url,
                      uploaded_at: f.uploaded_at || new Date().toISOString(),
                    }))
                : [];

              allResponses.push({
                id: q.response?.id || null,
                question: q.id,
                answer:
                  q.response?.performed === 'yes'
                    ? 'Yes'
                    : q.response?.performed === 'no'
                    ? 'No'
                    : '',
                evidence: q.response?.evidence_text || '',
                backendFiles,
              });
            });
          } catch {
            statusMap[domain.id] = 'draft';
          }
        }

        setAssessmentIds(idsMap);
        setSubmissionStatus(statusMap);
        setResponses(allResponses);
        if (domainsData.length > 0) setSelectedDomainId(domainsData[0].id);
      } catch (e) {
        console.error(e);
        setError('Failed to load COBIT assessment data.');
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [navigate, token]);

  const handleResponse = useCallback(
    (questionId, answer) => {
      const domain = getSelectedDomain();
      if (submissionStatus[domain?.id] === 'submitted') {
        toast.error('This process has been submitted and locked.');
        return;
      }

      setSaveState((prev) => ({ ...prev, hasUnsyncedChanges: true }));

      const base = responses.find((r) => r.question === questionId) || {
        id: null,
        question: questionId,
        answer: '',
        evidence: '',
        backendFiles: [],
      };
      const next = { ...base, answer };

      setResponses((prev) =>
        prev.some((r) => r.question === questionId)
          ? prev.map((r) => (r.question === questionId ? { ...r, answer } : r))
          : [...prev, next]
      );

      debouncedSave(next);
    },
    [getSelectedDomain, submissionStatus, responses, debouncedSave]
  );

  const handleEvidence = useCallback(
    (questionId, evidence) => {
      const domain = getSelectedDomain();
      if (submissionStatus[domain?.id] === 'submitted') {
        toast.error('This domain has been submitted and locked.');
        return;
      }

      setSaveState((prev) => ({ ...prev, hasUnsyncedChanges: true }));

      const base = responses.find((r) => r.question === questionId) || {
        id: null,
        question: questionId,
        answer: '',
        evidence: '',
        backendFiles: [],
      };
      const next = { ...base, evidence };

      setResponses((prev) =>
        prev.some((r) => r.question === questionId)
          ? prev.map((r) => (r.question === questionId ? { ...r, evidence } : r))
          : [...prev, next]
      );

      debouncedSave(next);
    },
    [getSelectedDomain, submissionStatus, responses, debouncedSave]
  );

  const handleFilesSelected = useCallback(
    (questionId, fileList) => {
      const domain = getSelectedDomain();
      if (submissionStatus[domain?.id] === 'submitted') {
        toast.error('This domain has been submitted and locked.');
        return;
      }

      const files = Array.from(fileList || []);
      if (!files.length) return;

      setSaveState((prev) => ({ ...prev, hasUnsyncedChanges: true }));

      const latest = responses.find((r) => r.question === questionId);
      if (latest) enqueueSave(latest);

      const oversized = files.filter((f) => f.size > MAX_FILE_SIZE);
      oversized.forEach((f) => toast.error(`✗ ${f.name} exceeds the maximum size of 150 MB.`));

      const allowedFiles = files.filter((f) => f.size <= MAX_FILE_SIZE);
      if (!allowedFiles.length) return;

      const result = uploadManager.addFiles({ questionId, files: allowedFiles });

      result.acceptedFiles?.forEach((f) => toast.success(`✓ ${f.name} — Queued`));
      result.rejectedFiles?.forEach((f) => toast.error(`✗ ${f.reason}`));
    },
    [getSelectedDomain, submissionStatus, responses, enqueueSave, uploadManager]
  );

  const retryPendingFile = useCallback(
    (questionId, taskId) => {
      const task = uploadManager.tasks.find((t) => t.id === taskId && t.questionId === questionId);
      if (task) uploadManager.retryUpload(task.id);
    },
    [uploadManager]
  );

  const removePendingFile = useCallback(
    (questionId, taskId) => {
      const task = uploadManager.tasks.find((t) => t.id === taskId && t.questionId === questionId);
      if (task) uploadManager.removeTask(task.id);
    },
    [uploadManager]
  );

  const handleRemoveBackendFile = useCallback(
    async (questionId, fileId) => {
      const domain = getSelectedDomain();
      if (submissionStatus[domain?.id] === 'submitted') {
        toast.error('Cannot modify submitted responses');
        return;
      }

      const response = responses.find((r) => r.question === questionId);
      if (!response?.id) {
        toast.error('Response not found');
        return;
      }

      try {
        await removeEvidenceFile({
          responseId: response.id,
          responseType: 'cobit',
          fileId,
        });

        setResponses((prev) =>
          prev.map((r) =>
            r.question === questionId
              ? { ...r, backendFiles: (r.backendFiles || []).filter((f) => f.id !== fileId) }
              : r
          )
        );
        setSaveState((prev) => ({ ...prev, hasUnsyncedChanges: true }));
        toast.success('File removed successfully');
      } catch (err) {
        console.error(err);
        toast.error(err.response?.data?.error || 'Failed to remove file');
      }
    },
    [getSelectedDomain, submissionStatus, responses]
  );

  const responsesWithUploadState = useMemo(() => {
    return responses.map((r) => {
      const uploads = uploadManager
        .getTasksByQuestion(r.question)
        .filter((t) => t.status !== 'success')
        .map((t) => ({
          tempId: t.id,
          name: t.name,
          size: t.size,
          status: t.status === 'canceled' ? 'failed' : t.status,
          error: t.error || '',
          progress: t.progress || 0,
        }));

      return { ...r, newFiles: uploads };
    });
  }, [responses, uploadManager]);

  const progress = useMemo(
    () => getDomainProgress(selectedDomain, responsesWithUploadState),
    [selectedDomain, responsesWithUploadState]
  );

  const incompleteQuestions = useMemo(
    () => getIncompleteQuestions(selectedDomain, responsesWithUploadState),
    [selectedDomain, responsesWithUploadState]
  );

  const submissionState = useMemo(() => {
    if (!selectedDomain) {
      return { canSubmit: false, reason: 'No domain selected.', pendingUploads: 0, uploadingCount: 0, failedUploads: 0, hasPendingUploads: false };
    }
    if (submissionStatus[selectedDomain.id] === 'submitted') {
      return { canSubmit: false, reason: 'Assessment already submitted.', pendingUploads: 0, uploadingCount: 0, failedUploads: 0, hasPendingUploads: false };
    }
    if (isBlockedByAuth) {
      return { canSubmit: false, reason: 'Session expired. Please sign in again.', pendingUploads: 0, uploadingCount: 0, failedUploads: 0, hasPendingUploads: false };
    }
    if (isOffline) {
      return { canSubmit: false, reason: 'You are offline. Reconnect before submitting.', pendingUploads: 0, uploadingCount: 0, failedUploads: 0, hasPendingUploads: false };
    }

    const questionIds = selectedDomain.practices?.flatMap((p) => (p.questions || []).map((q) => q.id)) || [];
    const domainTasks = uploadManager.tasks.filter((t) => questionIds.includes(t.questionId));

    const queued = domainTasks.filter((t) => t.status === 'queued').length;
    const uploading = domainTasks.filter((t) => t.status === 'uploading').length;
    const failed = domainTasks.filter((t) => t.status === 'failed').length;

    const hasPendingUploads = queued + uploading > 0;
    const hasIncomplete = incompleteQuestions.length > 0;

    let reason = '';
    if (hasIncomplete) reason = 'Complete all required questions/evidence.';
    else if (hasPendingUploads) reason = 'Wait for evidence uploads to finish.';
    else if (failed > 0) reason = 'Retry failed uploads before submitting.';
    else if (submitting) reason = 'Submitting...';
    else reason = 'Ready to submit.';

    return {
      canSubmit: !hasIncomplete && !hasPendingUploads && failed === 0 && !submitting,
      reason,
      pendingUploads: queued + uploading,
      uploadingCount: uploading,
      failedUploads: failed,
      hasPendingUploads,
    };
  }, [selectedDomain, submissionStatus, uploadManager.tasks, incompleteQuestions, submitting, isBlockedByAuth, isOffline]);

  const submitCurrentDomain = useCallback(async () => {
    const domain = getSelectedDomain();
    if (!domain) {
      toast.error('Process not found.');
      return false;
    }

    if (submissionStatus[domain.id] === 'submitted') {
      toast.warning('This process has already been submitted and locked.');
      return false;
    }

    if (!submissionState.canSubmit) {
      toast.warning(submissionState.reason || 'Not ready to submit.');
      return false;
    }

    const domainResponses = getResponsesForDomain(domain, responsesWithUploadState);
    const validationError = validateBeforeSubmit(domainResponses);
    if (validationError) {
      toast.warning(validationError);
      return false;
    }

    const payload = domainResponses.map((r) => ({
      cobit_question: r.question,
      performed: r.answer.toLowerCase(),
      evidence_text: r.evidence,
    }));

    try {
      setSubmitting(true);
      await submitCobitDomain(domain.id, {
        assessment_id: assessmentIds[domain.id],
        responses: payload,
      });

      setSubmissionStatus((prev) => ({ ...prev, [domain.id]: 'submitted' }));
      setSaveState({ hasUnsyncedChanges: false, blockedReason: null, lastError: null });
      toast.success(`Process "${domain.title}" submitted successfully!`);
      return true;
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Submission failed');
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [getSelectedDomain, submissionStatus, submissionState, responsesWithUploadState, assessmentIds]);

  return {
    domains,
    responses: responsesWithUploadState,
    selectedDomain,
    selectedDomainId,
    setSelectedDomainId,
    submissionStatus,
    loading,
    error,
    submitting,
    lastSaved,
    progress,
    incompleteQuestions,
    submissionState,

    saveState,
    isBlockedByAuth,
    isOffline,

    handleResponse,
    handleEvidence,
    handleFilesSelected,
    retryPendingFile,
    removePendingFile,
    handleRemoveBackendFile,
    submitCurrentDomain,

    uploadManager,

    getDomainProgress: (domainId) => {
      const domain = domains.find((d) => d.id === domainId);
      return getDomainProgress(domain, responsesWithUploadState);
    },
  };
};